'use client'

import { getIdToken } from 'firebase/auth'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { BsChatDotsFill } from 'react-icons/bs'
import { FaFire, FaPlay, FaLock, FaSmile, FaTimes } from 'react-icons/fa'
import { GiDress } from 'react-icons/gi'
import { MdOutlineSwipe, MdTransgender, MdWoman } from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import {
  capitalizeFirstLetter,
  capitalizeNames,
  getEmotionsString,
  getOpennessString,
  isUrl,
} from '../helpers/helpers'
import { TbPhotoMinus } from 'react-icons/tb'
import HoverVideoPreview from './HoverVideoPreview'

const ChatCard = ({
  id,
  imageUrl = [],
  video,
  showTutorial,
  name = '',
  age = '',
  location = '',
  personality = '',
  prompt = '',
  sexuality,
  occupation,
  gender,
  homeScreen,
  explore,
  onMatch,
  // Suppress the Free-user "go upgrade instead of matching" redirect.
  //
  // Set by the Telegram Mini App, for two reasons. Product: onMatch there means
  // "hand this companion to the bot and close", so a router.push to /upgrade
  // swallows the tap and the user just sees the card advance. Compliance: that
  // page is the WEB checkout, and Bot Developer ToS §6.2 requires digital goods
  // sold inside Telegram to go through Stars — rendering it in the webview is
  // the specific thing that gets a bot hidden from mobile users.
  //
  // Defaults false, so every existing caller behaves exactly as before.
  suppressUpgrade = false,
  scenario,
  emotions,
  openness,
  saves,
  avatar = false,
  index,
  homePage,
  portraitImage,
  staticPage = false,
  uid,
  safe = false,
  // A static Discover preview using this card's real design. The whole card
  // links to signup, without enabling matching, swiping, or member actions.
  signupOnly = false,
  imageAlt,
  // A published date game's pointer ({version, title, cover_url}) from profiles/{id}. When
  // present on an explore card, a Date button sits beside Chat.
  game,
}) => {
  const router = useRouter()
  const { t } = useTranslation()
  const videoRef = useRef(null)

  const [imageLoaded, setImageLoaded] = useState(false)
  const { user, subscription } = useAuth()
  const [isHovered, setIsHovered] = useState(false)

  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [currentImage, setCurrentImage] = useState(imageUrl[0])
  const [mouseDownPosition, setMouseDownPosition] = useState({
    x: null,
    y: null,
  })

  const totalSlides = imageUrl.length + 1
  const isEligibleForUpgrade =
    !signupOnly &&
    !homePage &&
    String(id)
      .split('')
      .reduce((sum, char) => sum + char.charCodeAt(0), 0) %
      2 ===
      0
  const isInfoCard = currentImageIndex >= imageUrl.length
  const aboutText = scenario?.trim() || prompt?.trim() || ''
  const shouldShowVideo =
    !signupOnly && Boolean(video) && !isInfoCard && (isMobile || isHovered)
  const CardContainer = signupOnly ? 'a' : 'div'
  const MatchAction = signupOnly ? 'div' : 'button'
  const imageSource = !currentImage
    ? '/assets/avatar.webp'
    : isUrl(currentImage)
      ? currentImage
      : `data:image/jpeg;base64,${currentImage}`

  useEffect(() => {
    setCurrentImage(imageUrl[0])
    setCurrentImageIndex(0)
  }, [imageUrl])

  useEffect(() => {
    const el = videoRef.current
    if (!el || !video) return

    if (shouldShowVideo) {
      const playPromise = el.play()
      if (playPromise?.catch) {
        playPromise.catch(() => {})
      }
    } else {
      el.pause()
    }
  }, [shouldShowVideo, video])

  useEffect(() => {
    const el = videoRef.current
    if (!el) return

    if (isInfoCard) {
      el.pause()
      el.currentTime = 0
    }
  }, [isInfoCard])

  const handleMouseDown = (e) => {
    if (!e.target.matches('.button-class')) {
      setMouseDownPosition({ x: e.clientX, y: e.clientY })
    }
  }

  const handleMouseUp = (e) => {
    if (!e.target.closest('.match-button')) {
      if (
        mouseDownPosition.x === e.clientX &&
        mouseDownPosition.y === e.clientY
      ) {
        handleImageClick()
      }
    }
  }

  const handleImageLoad = () => {
    setImageLoaded(true)
  }

  const handleImageClick = () => {
    if (
      explore &&
      isEligibleForUpgrade &&
      subscription?.plan === 'Free' &&
      !suppressUpgrade
    ) {
      router.push('/upgrade')
    }

    const nextIndex = (currentImageIndex + 1) % totalSlides
    setCurrentImageIndex(nextIndex)
    if (nextIndex < imageUrl.length) {
      setCurrentImage(imageUrl[nextIndex])
    }
  }

  const handleTouchStart = (e) => {
    const touch = e.touches[0]
    setMouseDownPosition({ x: touch.clientX, y: touch.clientY })
  }

  const handleTouchEnd = (e) => {
    const touch = e.changedTouches[0]
    if (
      mouseDownPosition.x === touch.clientX &&
      mouseDownPosition.y === touch.clientY
    ) {
      handleImageClick()
    }
  }

  const renderImageIndicators = () => {
    return Array.from({ length: totalSlides }, (_, index) => (
      <span
        key={index}
        className={`inline-block h-1 w-8 mx-1 rounded-full backdrop-blur-sm ${
          index === currentImageIndex ? 'bg-white' : 'bg-white/40'
        }`}
      />
    ))
  }

  const deleteImage = async () => {
    if (!user || user?.uid !== process.env.NEXT_PUBLIC_ADMIN_UID) return

    const idToken = await getIdToken(user)

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/delete_profile`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            imageId: id,
          }),
        },
      )

      if (response.ok) {
        if (typeof window !== 'undefined') window.location.reload()
      } else {
        console.error('Failed to delete the image')
      }
    } catch (error) {
      console.error('Error deleting the image:', error)
    }
  }

  const removeImage = async () => {
    if (!user || user?.uid !== process.env.NEXT_PUBLIC_ADMIN_UID) return

    const idToken = await getIdToken(user)

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/remove_profile_image`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify({
            characterId: id,
            imageUrl: currentImage,
          }),
        },
      )

      if (response.ok) {
        if (typeof window !== 'undefined') window.location.reload()
      } else {
        console.error('Failed to delete the image')
      }
    } catch (error) {
      console.error('Error deleting the image:', error)
    }
  }

  const renderContent = () => {
    const secondaryText = personality?.trim() || ''

    const detailChips = [
      sexuality && {
        key: 'sexuality',
        icon: (
          <MdTransgender className="mr-1.5 shrink-0 text-[11px] text-white" />
        ),
        label: capitalizeFirstLetter(sexuality),
      },
      gender && {
        key: 'gender',
        icon: <MdWoman className="mr-1.5 shrink-0 text-[11px] text-white" />,
        label: capitalizeFirstLetter(gender),
      },
      emotions && {
        key: 'emotions',
        icon: <FaSmile className="mr-1.5 shrink-0 text-[11px] text-white" />,
        label: getEmotionsString(emotions),
      },
      openness && {
        key: 'openness',
        icon: <GiDress className="mr-1.5 shrink-0 text-[11px] text-white" />,
        label: getOpennessString(openness),
      },
    ].filter(Boolean)

    if (currentImageIndex < imageUrl.length || imageUrl.length === 0) {
      return (
        <>
          <img
            src={imageSource}
            alt={imageAlt || name}
            loading={signupOnly ? 'lazy' : undefined}
            decoding={signupOnly ? 'async' : undefined}
            className={`absolute top-0 left-0 h-full w-full rounded-2xl object-cover object-center brightness-[0.9] transition-opacity duration-300 ease-in-out ${
              explore &&
              isEligibleForUpgrade &&
              subscription?.plan === 'Free' &&
              'blur-sm'
            } ${
              signupOnly || (imageLoaded && (!video || !shouldShowVideo))
                ? 'opacity-100'
                : 'opacity-0'
            }`}
            onLoad={handleImageLoad}
            style={{ zIndex: 2 }}
            draggable="false"
            onError={(error) => {
              console.log(error)
            }}
          />

          {signupOnly && video && (
            <HoverVideoPreview
              src={video}
              className="absolute inset-0 z-[2] h-full w-full rounded-2xl object-cover object-center brightness-[0.9]"
            />
          )}

          {!signupOnly && video && !isInfoCard && (
            <video
              key={`${id}-${currentImageIndex}-${isMobile ? 'mobile' : 'desktop'}`}
              ref={videoRef}
              src={video}
              muted
              loop
              playsInline
              autoPlay={isMobile}
              preload="metadata"
              className={`absolute top-0 left-0 h-full w-full rounded-2xl object-cover object-center brightness-[0.9] transition-opacity duration-300 ease-in-out ${
                explore &&
                isEligibleForUpgrade &&
                subscription?.plan === 'Free' &&
                'blur-sm'
              } ${shouldShowVideo ? 'opacity-100' : 'opacity-0'}`}
              style={{
                zIndex: 2,
                visibility: shouldShowVideo ? 'visible' : 'hidden',
              }}
            />
          )}
        </>
      )
    }

    return (
      <div
        className={`absolute inset-0 z-[2] flex h-full w-full flex-col overflow-hidden rounded-2xl bg-[var(--middle-bg-color)] transition-opacity duration-500 ease-in-out ${
          imageLoaded ? 'opacity-100' : 'opacity-0'
        } ${homeScreen || explore ? 'p-5' : 'p-4'}`}
      >
        {(occupation || location) && (
          <div className="mb-3 min-w-0 shrink-0">
            <p className="truncate text-sm font-medium text-white/75">
              {[occupation && capitalizeFirstLetter(occupation), location]
                .filter(Boolean)
                .join(' • ')}
            </p>
          </div>
        )}

        {aboutText && (
          <section className="mb-3 min-w-0 shrink-0">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/50">
              About
            </p>
            <p
              className={`text-sm leading-5 text-white/90 ${
                secondaryText ? 'line-clamp-5' : 'line-clamp-7'
              }`}
            >
              {capitalizeFirstLetter(aboutText)}
            </p>
          </section>
        )}

        {secondaryText && (
          <section className="mb-3 min-w-0 shrink-0">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/50">
              Personality
            </p>
            <p className="line-clamp-6 text-sm leading-5 text-white/90">
              {capitalizeFirstLetter(secondaryText)}
            </p>
          </section>
        )}

        {detailChips.length > 0 && (
          <section className="min-w-0">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/50">
              Details
            </p>

            <div className="flex flex-wrap gap-2">
              {detailChips.slice(0, 6).map((item) => (
                <div
                  key={item.key}
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-medium text-white/90 backdrop-blur-sm"
                >
                  <span className="shrink-0 text-white/70">{item.icon}</span>
                  <span className="truncate">{item.label}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    )
  }

  if (avatar) {
    return (
      <img
        src={imageSource}
        className="object-cover object-top rounded-xl mr-2"
      />
    )
  }

  return (
    <>
      <CardContainer
        href={signupOnly ? '/signup' : undefined}
        aria-label={
          signupOnly
            ? `Sign up to chat with ${capitalizeNames(name)}`
            : undefined
        }
        className={`block overflow-hidden rounded-2xl z-10 group ${
          explore || signupOnly ? '' : 'hover:scale-[1.02]'
        } relative cursor-pointer select-none transition-all duration-200 ${
          !homeScreen ? 'shadow-xl' : ''
        } ${!explore ? 'hover:shadow-2xl' : ''} ${
          signupOnly
            ? 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white'
            : ''
        }`}
        style={signupOnly ? undefined : { minHeight: '300px' }}
        onMouseEnter={
          !signupOnly && !isMobile ? () => setIsHovered(true) : null
        }
        onMouseLeave={
          !signupOnly && !isMobile ? () => setIsHovered(false) : null
        }
        onTouchStart={!signupOnly && isMobile ? handleTouchStart : null}
        onTouchEnd={!signupOnly && isMobile ? handleTouchEnd : null}
        onMouseDown={!signupOnly && !isMobile ? handleMouseDown : null}
        onMouseUp={!signupOnly && !isMobile ? handleMouseUp : null}
      >
        <span
          className={`relative group overflow-hidden rounded-2xl bg-white w-60 transition-all duration-300 ease-in-out min-w-0 ${
            !signupOnly &&
            showTutorial &&
            !isHovered &&
            !isMobile &&
            'blur-[1px]'
          }`}
        >
          <div key={id} className="cursor-pointer">
            <div
              style={{
                position: 'relative',
                width: '100%',
                paddingTop: '150%',
              }}
            >
              <div
                className={`grid absolute top-0 left-0 w-full h-full ${
                  signupOnly || imageLoaded ? 'hidden' : ''
                } rounded-2xl`}
                style={{
                  gridTemplateColumns: '2fr 3fr',
                  backgroundColor: 'var(--foreground-bg-color)',
                  zIndex: 1,
                }}
              />

              {renderContent()}

              {(signupOnly || imageLoaded) && (
                <div style={{ width: '100%' }}>
                  {!signupOnly && (
                    <div className="absolute top-0 left-0 right-0 flex justify-center mt-3 z-50 px-3">
                      {renderImageIndicators()}
                    </div>
                  )}

                  {currentImageIndex < imageUrl.length && (
                    <div className="absolute inset-x-0 bottom-0 z-10">
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent rounded-b-2xl pointer-events-none" />

                      <div
                        className={`relative pt-16 text-white ${
                          signupOnly
                            ? 'px-3 pb-14 sm:px-4 sm:pb-16'
                            : 'px-4 pb-16'
                        }`}
                      >
                        <div className="flex items-baseline gap-2 leading-tight tracking-tight">
                          <span
                            className={`font-extrabold drop-shadow-sm ${
                              signupOnly ? 'text-lg sm:text-2xl' : 'text-2xl'
                            }`}
                          >
                            {capitalizeNames(name)}
                          </span>

                          {age && (
                            <span
                              className={`font-bold text-white/80 drop-shadow-sm ${
                                signupOnly ? 'text-base sm:text-xl' : 'text-xl'
                              }`}
                            >
                              {age}
                            </span>
                          )}
                        </div>

                        <p
                          className={`font-medium text-white/70 drop-shadow-sm tracking-tight line-clamp-2 ${
                            signupOnly
                              ? 'text-xs leading-4 sm:text-sm sm:leading-5'
                              : 'text-sm leading-5'
                          }`}
                        >
                          {capitalizeFirstLetter(aboutText)}
                        </p>
                      </div>
                    </div>
                  )}

                  {explore &&
                    isEligibleForUpgrade &&
                    subscription?.plan === 'Free' && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/35 z-20">
                        <FaLock className="text-white text-4xl opacity-80" />
                        <p className="text-white font-semibold text-lg mt-2 opacity-80">
                          Upgrade to Chat
                        </p>
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
        </span>

        {!signupOnly && (
          <div
            className={`absolute top-0 left-0 w-full h-full flex items-center justify-center rounded-2xl transition-opacity duration-300 ${
              showTutorial && !isHovered && !isMobile
                ? 'opacity-100'
                : 'opacity-0'
            }`}
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              zIndex: 3,
            }}
          >
            <div className="text-center text-white justify-center items-center flex flex-col cursor-pointer">
              <MdOutlineSwipe className="text-5xl mb-5" />
              <p className="font-bold text-xl">Swipe</p>
              <p>Left to skip, Right to chat</p>
            </div>
          </div>
        )}

        {(explore || signupOnly) && (
          <div className="absolute bottom-3 right-3 z-20 flex items-center gap-2">
            {/* Date beside Chat when the character has a published game. Free users and the
              "Upgrade to Chat" cards keep it: a date is free to play up to its final scene.
              `match-button` keeps the card's slide-advance (handleMouseUp) off it; the touch
              handler does not check that class, so the touch end stops here instead. */}
            {explore && !signupOnly && game && (
              <button
                type="button"
                className="match-button hover:scale-[1.06] transition-all duration-100"
                data-track="stories.chat_card"
                aria-label={t('dateButtonAria', {
                  name: capitalizeNames(name),
                })}
                onClick={(e) => {
                  e.stopPropagation()
                  router.push(`/date/${encodeURIComponent(id)}`)
                }}
                onTouchEnd={(e) => e.stopPropagation()}
              >
                <div className="bg-black/25 backdrop-blur-md hover:bg-[var(--main-bg-color)] text-white font-bold rounded-full h-10 px-3.5 items-center justify-center flex transition-colors duration-300 border border-white/10">
                  <FaPlay className="text-xs mr-1.5" />
                  <p className="font-semibold text-sm">{t('dateButton')}</p>
                </div>
              </button>
            )}
            <MatchAction
              className={`match-button transition-all duration-100 ${
                signupOnly ? '' : 'hover:scale-[1.06]'
              }`}
              onClick={
                signupOnly
                  ? undefined
                  : (e) => {
                      e.stopPropagation()
                      if (
                        explore &&
                        isEligibleForUpgrade &&
                        subscription?.plan === 'Free' &&
                        !suppressUpgrade
                      ) {
                        router.push('/upgrade')
                      } else {
                        onMatch()
                      }
                    }
              }
            >
              <div className="bg-black/25 backdrop-blur-md group-hover:bg-[var(--main-bg-color)] text-white font-bold rounded-full h-10 px-4 items-center justify-center flex transition-colors duration-300 border border-white/10">
                <BsChatDotsFill className="text-xl mr-1" />
                <p className="font-semibold text-sm">Chat</p>
              </div>
            </MatchAction>
          </div>
        )}

        {(explore || homeScreen) && (!signupOnly || saves != null) && (
          <div className="absolute bottom-3 left-3 z-20">
            <div className="text-white rounded-full px-3 py-1.5 flex items-center space-x-2 text-xs font-semibold bg-black/25 backdrop-blur-md border border-white/10">
              <FaFire className="text-sm mr-1" />
              {saves}
            </div>
          </div>
        )}

        {!signupOnly && user?.uid === process.env.NEXT_PUBLIC_ADMIN_UID && (
          <button
            className="match-button absolute top-4 right-4 hover:scale-[1.04] z-30 transition-all duration-100"
            onClick={(e) => {
              e.stopPropagation()
              deleteImage()
            }}
          >
            <div className="bg-white text-red-500 font-bold rounded-full h-8 w-8 items-center justify-center flex shadow-md">
              <FaTimes className="text-xl" />
            </div>
          </button>
        )}

        {!signupOnly &&
          !explore &&
          currentImageIndex > 0 &&
          currentImageIndex !== imageUrl.length &&
          user?.uid === uid && (
            <button
              className="match-button absolute bottom-4 right-4 hover:scale-[1.04] z-20 transition-all duration-100"
              onClick={(e) => {
                e.stopPropagation()
                removeImage()
              }}
            >
              <div className="bg-[var(--foreground-bg-color)] text-white font-bold rounded-full h-8 w-8 items-center justify-center flex shadow-md">
                <TbPhotoMinus className="text-xl" />
              </div>
            </button>
          )}
      </CardContainer>
    </>
  )
}

export default ChatCard
